import { MESSAGES } from "../../messages";
import { TagProps } from "../../components/visuals/CompassTag";
import {
  EngagementScreenData,
  VotingAlignmentCompany,
} from "../../models/stewardshipModels";
import {
  CellType,
  ColumnType as CustomColumnType,
  StewardshipScreenerDataSource,
  TableCellType,
  VotingCompanySummaryDataSource,
} from "../../models/tableModels";
import { statusBadgeColors } from "../../styles";
import { Filter } from "../../types/PageFilterTypes";
import voteIcon from "../../assets/svg/voting.svg";
import { FundManagerBadgeProps } from "../../components/visuals/FundManagerBadgeList";

export const getColumnTypeFromKeyOrValue = (
  column: string,
  value: CellType,
  columnTypes: CustomColumnType[] = [],
  loading = false,
): CustomColumnType => {
  if (loading) return { columnKey: column, type: TableCellType.LOADING };
  if (value === MESSAGES.NOT_AVAILABLE)
    return { columnKey: column, type: TableCellType.TEXT };

  for (const columnType of columnTypes) {
    if (columnType.columnKey === column) {
      return columnType;
    }
  }

  // Fallback based on the type of value
  if (typeof value === "number") {
    return { columnKey: column, type: TableCellType.NUMBER };
  }
  return { columnKey: column, type: TableCellType.TEXT };
};

export const buildStewardShipScreenerDataSource = (
  inputData: EngagementScreenData[],
  fundOptions: Filter[],
  managerOptions: Filter[],
  areOptionsLoading = false
): StewardshipScreenerDataSource[] => {
  return inputData.map((data) => {
    const rowManagerOptions = managerOptions.filter(
      (option) => !!option.key && data.manager_id.includes(option.key)
    );

    return {
      ...data,
      key: data.company,
      company: {
        name: data.company_name,
        logoUrl: data.company_image,
        companyId: data.company,
      },
      portfolio_index_id: buildFundManagerBadges(
        data.portfolio_index_id,
        fundOptions,
        managerOptions,
        rowManagerOptions,
        areOptionsLoading
      ),
      manager_id: rowManagerOptions.map((option) => ({
        username: option.displayValue,
        image: option.selectorName,
        tooltip: true,
      })),
    };
  });
};

const buildFundManagerBadges = (
  fundIds: string[],
  fundOptions: Filter[],
  managerOptions: Filter[],
  rowManagerOptions: Filter[],
  areOptionsLoading: boolean
): FundManagerBadgeProps[] =>
  fundIds.map((fundId) => {
    if (areOptionsLoading) {
      return { fundId, fundName: fundId, isLoading: true };
    }

    const fund = fundOptions.find((option) => option.key === fundId);
    const manager = resolveFundManager(
      fundId,
      fund,
      managerOptions,
      rowManagerOptions,
    );

    return {
      fundId,
      fundName: fund?.displayValue || fundId,
      managerImage: getManagerImage(manager),
      managerName: manager?.displayValue,
    };
  });

const normalizeName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const getNameInitials = (value: string): string =>
  value
    .split(/[^a-z0-9]+/i)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toLowerCase();

const MANAGER_NAME_SUFFIXES = new Set([
  "asset",
  "assets",
  "capital",
  "group",
  "investment",
  "investments",
  "limited",
  "ltd",
  "management",
  "partners",
  "plc",
]);

const MANAGER_IMAGE_FALLBACKS: Record<string, string> = {
  mginvestments: "https://www.google.com/s2/favicons?domain=mandg.com&sz=128",
};

const getManagerImage = (manager: Filter | undefined): string | undefined => {
  if (!manager) return undefined;

  const fallbackImage = MANAGER_IMAGE_FALLBACKS[normalizeName(manager.displayValue)];
  const isBlockedImageHost = manager.selectorName?.includes(
    "yt3.googleusercontent.com",
  );

  return isBlockedImageHost && fallbackImage
    ? fallbackImage
    : manager.selectorName;
};

const getManagerAliases = (managerName: string): string[] => {
  const words = managerName.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const brandName = words
    .filter((word) => !MANAGER_NAME_SUFFIXES.has(word))
    .join("");

  return Array.from(
    new Set([normalizeName(managerName), getNameInitials(managerName), brandName]),
  ).filter((alias) => alias.length >= 2);
};

const resolveFundManager = (
  fundId: string,
  fund: Filter | undefined,
  managerOptions: Filter[],
  rowManagerOptions: Filter[],
): Filter | undefined => {
  const directManager = managerOptions.find(
    (manager) => !!fund?.selectorName && manager.key === fund.selectorName,
  );
  if (directManager) return directManager;

  if (rowManagerOptions.length === 1) return rowManagerOptions[0];

  const normalizedFundName = normalizeName(fund?.displayValue || fundId);
  return rowManagerOptions.find((manager) => {
    return getManagerAliases(manager.displayValue).some((managerAlias) =>
      normalizedFundName.includes(managerAlias),
    );
  });
};

export const buildVotingCompanySummaryDataSource = (
  inputData: VotingAlignmentCompany[],
): VotingCompanySummaryDataSource[] => {
  return inputData.map((data) => {
    return {
      ...data,
      key: data.compass_cid,
      company: {
        name: data.name,
        logoUrl: data.image,
        companyId: data.compass_cid,
      },
      alignedPct: data.alignedPct || 0,
      aligned_votes_count: data.aligned_votes_count || 0,
      misaligned_votes_count:
        (data.total_votes_count || 0) - (data.aligned_votes_count || 0),
      total_votes_count: {
        tagKey: `${data.total_votes_count.toString()} Votes`,
        color: statusBadgeColors.Planned.text,
        bg: statusBadgeColors.Planned.bg,
        displayValue: "",
        shouldDisplayKey: true,
        icon: { src: voteIcon, size: 17 },
      },
      manager: data.manager,
      countryIso2: data.geo_level_2,
      totalRecordsCount: data.total_count,
      categories: buildTagProps(
        data.categories,
        data.categories.map((category) => ({
          key: category,
          displayValue: category,
        })),
      ),
    };
  });
};

const buildTagProps = (items: string[], options: Filter[]): TagProps[] => {
  // Convert the colors object to an array of color options
  const colorOptions = Object.values(statusBadgeColors);

  return items.map((item, index) => {
    const color = colorOptions[index % colorOptions.length];
    return {
      tagKey: item,
      color: color.text,
      bg: color.bg,
      displayValue:
        options.find((option) => option.key === item)?.displayValue || item,
    };
  });
};

export const generateFakeEngagementData = (
  rows = 30,
): EngagementScreenData[] => {
  return Array.from({ length: rows }).map((_, index) => ({
    company: `Company${index + 1}`,
    company_name: `Company Name ${index + 1}`,
    company_image: `https://example.com/image${index + 1}.png`,
    manager_id: [
      `manager${(index % 5) + 1}`,
      `manager${((index + 1) % 5) + 1}`,
    ],
    portfolio_index_id: [
      `portfolio${(index % 3) + 1}`,
      `portfolio${((index + 2) % 3) + 1}`,
    ],
    completed_milestones: index % 10,
    voh: (index % 100) + 1,
    engagement_count: (index % 20) + 1,
    corporate_objective_count: (index % 15) + 1,
  }));
};
